/**
 * V1.0.37: Service cho module Hộp thư lắng nghe (Listening Mailbox).
 * 2 nhóm endpoint:
 *  - /api/public/feedback/*    — không cần auth (trang /feedback)
 *  - /api/admin/feedback/*     — cần auth + permission
 */
import { apiClient } from './apiClient';

// ============================================================
// Types
// ============================================================

export type Sentiment = 'POSITIVE' | 'SUGGESTION' | 'IDEA' | 'COMPLAINT';
export type FeedbackStatus = 'NEW' | 'PROCESSING' | 'RESOLVED' | 'IGNORED' | 'ARCHIVED';
export type FeedbackPriority = 'LOW' | 'NORMAL' | 'HIGH';

/** V1.0.38: Khoa liên quan đến chủ đề */
export interface FeedbackTopicDepartment {
  departmentId: string;
  departmentCode: string;
  departmentName: string;
  sortOrder: number;
  managerId?: string | null;
  managerName?: string | null;
}

export interface FeedbackTopic {
  id: string;
  code: string;
  name: string;
  emoji?: string | null;
  description?: string | null;
  listeningMessage?: string | null;
  isActive: boolean;
  targetGroups: string[];
  sortOrder: number;
  feedbackCount: number;
  createdAt: string;
  /** V1.0.38: Khoa liên quan (sortOrder=0 là khoa chính) */
  relatedDepartments?: FeedbackTopicDepartment[];
}

export interface CreateFeedbackTopicRequest {
  name: string;
  emoji?: string;
  description?: string;
  listeningMessage?: string;
  isActive?: boolean;
  targetGroups?: string[];
  sortOrder?: number;
  /** V1.0.38: thứ tự khoa = ưu tiên (khoa đầu = khoa chính) */
  relatedDepartmentIds?: string[];
}

export type UpdateFeedbackTopicRequest = Omit<CreateFeedbackTopicRequest, 'isActive'> & {
  isActive: boolean;
};

export interface FeedbackAttachment {
  id: string;
  fileName: string;
  fileUrl: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  uploadedAt: string;
}

export interface Feedback {
  id: string;
  code: string;
  topicId?: string | null;
  topicName?: string | null;
  topicEmoji?: string | null;
  sentiment: Sentiment;
  content: string;
  isAnonymous: boolean;
  senderName?: string | null;
  senderPhone?: string | null;
  departmentVisited?: string | null;
  departmentName?: string | null;
  status: FeedbackStatus;
  priority: FeedbackPriority;
  hashtags: string[];
  submittedAt: string;
  assignedTo?: string | null;
  assignedToName?: string | null;
  assignedAt?: string | null;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  resolvedByName?: string | null;
  resolutionNote?: string | null;
  attachments: FeedbackAttachment[];
}

export interface SubmitFeedbackRequest {
  topicId?: string;
  sentiment: Sentiment;
  content: string;
  isAnonymous: boolean;
  senderName?: string;
  senderPhone?: string;
  departmentVisited?: string;
  hashtags?: string[];
}

export interface SubmitFeedbackResponse {
  id: string;
  code: string;
  message: string;
}

export interface FeedbackInboxQuery {
  status?: FeedbackStatus;
  sentiment?: Sentiment;
  topicId?: string;
  departmentId?: string;
  search?: string;
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}

export interface FeedbackStats {
  totalMessages: number;
  positiveCount: number;
  suggestionCount: number;
  complaintCount: number;
  ideaCount: number;
  newCount: number;
  processingCount: number;
  resolvedCount: number;
  positivePercent: number;
  complaintPercent: number;
}

export interface PublicFeedbackTopic {
  id: string;
  code: string;
  name: string;
  emoji?: string | null;
  description?: string | null;
  listeningMessage?: string | null;
}

export interface PublicDepartment {
  id: string;
  code: string;
  name: string;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ============================================================
// Public service (no auth)
// ============================================================

export const publicFeedbackService = {
  async listTopics(): Promise<PublicFeedbackTopic[]> {
    const { data } = await apiClient.get<PublicFeedbackTopic[]>('/public/feedback/topics');
    return data;
  },

  async listDepartments(): Promise<PublicDepartment[]> {
    const { data } = await apiClient.get<PublicDepartment[]>('/public/feedback/departments');
    return data;
  },

  async submit(payload: SubmitFeedbackRequest): Promise<SubmitFeedbackResponse> {
    const { data } = await apiClient.post<SubmitFeedbackResponse>('/public/feedback', payload);
    return data;
  },

  async uploadAttachment(feedbackId: string, file: File): Promise<FeedbackAttachment> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post<FeedbackAttachment>(
      `/public/feedback/${feedbackId}/attachments`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  },
};

// ============================================================
// Admin service (cần auth)
// ============================================================

export const adminFeedbackService = {
  // --- Topics ---
  async listTopics(includeInactive = true): Promise<FeedbackTopic[]> {
    const { data } = await apiClient.get<FeedbackTopic[]>('/admin/feedback/topics', {
      params: { includeInactive },
    });
    return data;
  },

  async getTopic(id: string): Promise<FeedbackTopic> {
    const { data } = await apiClient.get<FeedbackTopic>(`/admin/feedback/topics/${id}`);
    return data;
  },

  async createTopic(req: CreateFeedbackTopicRequest): Promise<FeedbackTopic> {
    const { data } = await apiClient.post<FeedbackTopic>('/admin/feedback/topics', req);
    return data;
  },

  async updateTopic(id: string, req: UpdateFeedbackTopicRequest): Promise<FeedbackTopic> {
    const { data } = await apiClient.patch<FeedbackTopic>(`/admin/feedback/topics/${id}`, req);
    return data;
  },

  async toggleTopic(id: string): Promise<FeedbackTopic> {
    const { data } = await apiClient.patch<FeedbackTopic>(`/admin/feedback/topics/${id}/toggle`);
    return data;
  },

  async deleteTopic(id: string): Promise<void> {
    await apiClient.delete(`/admin/feedback/topics/${id}`);
  },

  /** V1.0.43: Đổi STT chủ đề lên/xuống */
  async moveTopic(id: string, direction: 'up' | 'down'): Promise<FeedbackTopic[]> {
    const { data } = await apiClient.patch<FeedbackTopic[]>(
      `/admin/feedback/topics/${id}/move`,
      undefined,
      { params: { direction } }
    );
    return data;
  },

  // --- Inbox ---
  async listInbox(query: FeedbackInboxQuery = {}): Promise<Paginated<Feedback>> {
    const { data } = await apiClient.get<Paginated<Feedback>>('/admin/feedback', { params: query });
    return data;
  },

  async getById(id: string): Promise<Feedback> {
    const { data } = await apiClient.get<Feedback>(`/admin/feedback/${id}`);
    return data;
  },

  async updateStatus(id: string, status: FeedbackStatus, note?: string): Promise<Feedback> {
    const { data } = await apiClient.patch<Feedback>(`/admin/feedback/${id}/status`, { status, note });
    return data;
  },

  async assign(id: string, assignedTo: string): Promise<Feedback> {
    const { data } = await apiClient.patch<Feedback>(`/admin/feedback/${id}/assign`, { assignedTo });
    return data;
  },

  async stats(fromDate?: string, toDate?: string): Promise<FeedbackStats> {
    const { data } = await apiClient.get<FeedbackStats>('/admin/feedback/stats', {
      params: { fromDate, toDate },
    });
    return data;
  },

  async exportExcel(query: FeedbackInboxQuery = {}): Promise<Blob> {
    const { data } = await apiClient.get('/admin/feedback/export', {
      params: query,
      responseType: 'blob',
    });
    return data as Blob;
  },
};

// ============================================================
// UI labels
// ============================================================

export const SENTIMENT_LABELS: Record<Sentiment, string> = {
  POSITIVE: 'Khen ngợi',
  SUGGESTION: 'Góp ý chung',
  IDEA: 'Ý tưởng mới',
  COMPLAINT: 'Phàn nàn',
};

export const SENTIMENT_COLORS: Record<Sentiment, string> = {
  POSITIVE: '#52c41a',
  SUGGESTION: '#faad14',
  IDEA: '#1976D2',
  COMPLAINT: '#f5222d',
};

export const STATUS_LABELS: Record<FeedbackStatus, string> = {
  NEW: 'Mới',
  PROCESSING: 'Đang xử lý',
  RESOLVED: 'Đã xử lý',
  IGNORED: 'Bỏ qua',
  ARCHIVED: 'Lưu trữ',
};

export const STATUS_COLORS: Record<FeedbackStatus, string> = {
  NEW: '#1e6feb',
  PROCESSING: '#faad14',
  RESOLVED: '#52c41a',
  IGNORED: '#7d8a98',
  ARCHIVED: '#9e9e9e',
};

// ============================================================
// V1.0.38 — Kanban + Linked Tasks
// ============================================================

export interface FeedbackKanbanColumn {
  topicId?: string | null;
  topicName: string;
  topicEmoji?: string | null;
  totalCount: number;
  items: Feedback[];
}

export interface FeedbackLinkedTask {
  id: string;
  taskId: string;
  taskCode: string;
  taskTitle: string;
  taskStatus: string;
  taskPriority: string;
  assigneeId?: string | null;
  assigneeName?: string | null;
  departmentId?: string | null;
  departmentName?: string | null;
  dueDate?: string | null;
  createdAt: string;
  createdBy?: string | null;
  createdByName?: string | null;
}

export interface CreateTaskFromFeedbackRequest {
  title?: string;
  description?: string;
  departmentId?: string;
  assigneeId?: string;
  dueDate?: string;
  startDate?: string;
  /** V1.0.39: enum DB chuẩn — LOW | MEDIUM | HIGH | CRITICAL */
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  category?: string;
  watcherIds?: string[];
  tags?: string[];
  estimatedHours?: number;
  requiresSignature?: boolean;
  /** Cấp Gấp 1–5 */
  priorityLevel?: number;
  /** Cấp Quan trọng 1–5 */
  importantLevel?: number;
  /** KPI doanh thu (VND) */
  kpiRevenuePlan?: number;
  /** Gán vào project */
  projectId?: string;
}

// Extend adminFeedbackService với 4 method mới
(adminFeedbackService as any).kanban = async (query: FeedbackInboxQuery = {}): Promise<FeedbackKanbanColumn[]> => {
  const { data } = await apiClient.get<FeedbackKanbanColumn[]>('/admin/feedback/kanban', { params: query });
  return data;
};
(adminFeedbackService as any).archive = async (id: string): Promise<Feedback> => {
  const { data } = await apiClient.patch<Feedback>(`/admin/feedback/${id}/archive`);
  return data;
};
(adminFeedbackService as any).createTaskFrom = async (id: string, req: CreateTaskFromFeedbackRequest): Promise<FeedbackLinkedTask> => {
  const { data } = await apiClient.post<FeedbackLinkedTask>(`/admin/feedback/${id}/tasks`, req);
  return data;
};
(adminFeedbackService as any).listLinkedTasks = async (id: string): Promise<FeedbackLinkedTask[]> => {
  const { data } = await apiClient.get<FeedbackLinkedTask[]>(`/admin/feedback/${id}/tasks`);
  return data;
};

// Type augmentation
export type AdminFeedbackServiceV2 = typeof adminFeedbackService & {
  kanban(query?: FeedbackInboxQuery): Promise<FeedbackKanbanColumn[]>;
  archive(id: string): Promise<Feedback>;
  createTaskFrom(id: string, req: CreateTaskFromFeedbackRequest): Promise<FeedbackLinkedTask>;
  listLinkedTasks(id: string): Promise<FeedbackLinkedTask[]>;
};
export const adminFeedbackV2 = adminFeedbackService as AdminFeedbackServiceV2;
